import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/lang";
import { EASE } from "@/components/Reveal";
import { GROUPS } from "@/catalogData";
import { PHONE, PHONE_TEL } from "@/content";

export default function Navbar() {
    const { c, lang, setEn, setEs } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [prodOpen, setProdOpen] = useState(false);
    const [prodSubOpen, setProdSubOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setProdOpen(false);
        setProdSubOpen(false);
    }, [pathname]);

    const links = [
        { to: "/", label: c.nav.home, id: "home" },
        { to: "/about", label: c.nav.about, id: "about" },
        { to: "/products", label: c.nav.products, id: "products" },
        { to: "/career", label: c.nav.career, id: "career" },
        { to: "/contact", label: c.nav.contact, id: "contact" },
    ];
    const productsActive = pathname.startsWith("/products");

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "border-b border-line bg-pure/85 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="container-x flex h-[76px] items-center justify-between">
                    <Link to="/" data-testid="nav-link-home" aria-label="Star USA home">
                        <Logo variant={scrolled || open ? "dark" : "light"} className="h-11 w-auto sm:h-12" />
                    </Link>

                    <nav className="hidden items-center gap-9 lg:flex">
                        {links
                            .filter((l) => ["home", "about"].includes(l.id))
                            .map((l) => (
                                <NavLink
                                    key={l.id}
                                    to={l.to}
                                    data-testid={`nav-link-${l.id}`}
                                    className={({ isActive }) =>
                                        `group relative text-sm font-medium tracking-wide transition-colors ${
                                            isActive
                                                ? scrolled
                                                    ? "text-sky-text"
                                                    : "text-sky"
                                                : scrolled
                                                    ? "text-ink-soft hover:text-ink"
                                                    : "text-paper/80 hover:text-paper"
                                        }`
                                    }
                                >
                                    {l.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${
                                            scrolled ? "bg-sky-text" : "bg-sky"
                                        }`}
                                    />
                                </NavLink>
                            ))}
                        <div
                            className="relative"
                            onMouseEnter={() => setProdOpen(true)}
                            onMouseLeave={() => setProdOpen(false)}
                        >
                            <Link
                                to="/products"
                                data-testid="nav-link-products"
                                className={`flex items-center gap-1.5 text-sm font-medium tracking-wide transition-colors ${
                                    productsActive
                                        ? scrolled
                                            ? "text-sky-text"
                                            : "text-sky"
                                        : scrolled
                                            ? "text-ink-soft hover:text-ink"
                                            : "text-paper/80 hover:text-paper"
                                }`}
                            >
                                {c.nav.products}
                                <ChevronDown
                                    className={`h-3.5 w-3.5 transition-transform duration-300 ${
                                        prodOpen ? "rotate-180" : ""
                                    }`}
                                />
                            </Link>
                            <AnimatePresence>
                                {prodOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 8 }}
                                        transition={{ duration: 0.2, ease: EASE }}
                                        className="absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-4"
                                        data-testid="nav-products-dropdown"
                                    >
                                        <div className="overflow-hidden rounded-2xl border border-line bg-pure shadow-lift">
                                            {GROUPS.map((g) => (
                                                <Link
                                                    key={g.key}
                                                    to={`/products?group=${g.key}`}
                                                    onClick={() => setProdOpen(false)}
                                                    data-testid={`nav-dropdown-${g.key}`}
                                                    className="flex items-center justify-between px-5 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-paper"
                                                >
                                                    {lang === "es" ? g.es : g.en}
                                                    <ArrowRight className="h-3.5 w-3.5 text-sky-text" />
                                                </Link>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {links
                            .filter((l) => ["career", "contact"].includes(l.id))
                            .map((l) => (
                                <NavLink
                                    key={l.id}
                                    to={l.to}
                                    data-testid={`nav-link-${l.id}`}
                                    className={({ isActive }) =>
                                        `group relative text-sm font-medium tracking-wide transition-colors ${
                                            isActive
                                                ? scrolled
                                                    ? "text-sky-text"
                                                    : "text-sky"
                                                : scrolled
                                                    ? "text-ink-soft hover:text-ink"
                                                    : "text-paper/80 hover:text-paper"
                                        }`
                                    }
                                >
                                    {l.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${
                                            scrolled ? "bg-sky-text" : "bg-sky"
                                        }`}
                                    />
                                </NavLink>
                            ))}
                    </nav>

                    <div className="flex items-center gap-3 sm:gap-4">
                        <div
                            className={`flex items-center rounded-full border p-1 backdrop-blur-md transition-colors ${
                                scrolled ? "border-line bg-white/80" : "border-paper/30 bg-paper/10"
                            }`}
                            data-testid="language-toggle-button"
                        >
                            {[
                                { id: "en", set: setEn, label: "EN" },
                                { id: "es", set: setEs, label: "ES" },
                            ].map((o) => (
                                <button
                                    key={o.id}
                                    onClick={o.set}
                                    data-testid={`language-option-${o.id}`}
                                    className={`rounded-full px-3 py-1.5 text-[11px] font-semibold tracking-[0.12em] transition-colors ${
                                        lang === o.id
                                            ? scrolled
                                                ? "bg-ink text-paper"
                                                : "bg-paper text-ink"
                                            : scrolled
                                                ? "text-mist hover:text-ink"
                                                : "text-paper/70 hover:text-paper"
                                    }`}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>

                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="nav-phone-cta"
                            className={`hidden items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 md:flex ${
                                scrolled
                                    ? "bg-ink text-paper hover:bg-sky-deep"
                                    : "bg-paper text-ink hover:bg-sky"
                            }`}
                        >
                            <Phone className="h-4 w-4" strokeWidth={2.5} />
                            {c.nav.call}
                        </a>

                        <button
                            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors lg:hidden ${
                                scrolled ? "text-ink" : "text-paper"
                            }`}
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-toggle"
                            aria-label={open ? c.nav.close : c.nav.menu}
                        >
                            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: EASE }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-pure/97 px-8 backdrop-blur-2xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-1">
                            {links.map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                                >
                                    {l.id === "products" ? (
                                        <div>
                                            <button
                                                onClick={() => setProdSubOpen(!prodSubOpen)}
                                                data-testid="mobile-nav-link-products"
                                                className="flex w-full items-center justify-between py-2 text-4xl font-light tracking-tight text-ink"
                                            >
                                                {l.label}
                                                <ChevronDown
                                                    className={`h-6 w-6 text-sky-text transition-transform duration-300 ${
                                                        prodSubOpen ? "rotate-180" : ""
                                                    }`}
                                                />
                                            </button>
                                            <AnimatePresence>
                                                {prodSubOpen && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: "auto", opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.3, ease: EASE }}
                                                        className="overflow-hidden"
                                                    >
                                                        <div className="ml-1 flex flex-col gap-1 border-l border-line py-1 pl-4">
                                                            {GROUPS.map((g) => (
                                                                <Link
                                                                    key={g.key}
                                                                    to={`/products?group=${g.key}`}
                                                                    onClick={() => setOpen(false)}
                                                                    data-testid={`mobile-nav-sublink-${g.key}`}
                                                                    className="flex items-center gap-2 py-1.5 text-xl text-ink-soft transition-colors hover:text-sky-text"
                                                                >
                                                                    {lang === "es" ? g.es : g.en}
                                                                    <ArrowRight className="h-3.5 w-3.5 text-sky-text" />
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ) : (
                                        <NavLink
                                            to={l.to}
                                            data-testid={`mobile-nav-link-${l.id}`}
                                            className={({ isActive }) =>
                                                `block py-2 text-4xl font-light tracking-tight ${
                                                    isActive ? "text-sky-text" : "text-ink"
                                                }`
                                            }
                                        >
                                            {l.label}
                                        </NavLink>
                                    )}
                                </motion.div>
                            ))}
                        </nav>
                        <motion.a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="mobile-phone-cta"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
                            className="mt-10 flex items-center justify-center gap-3 rounded-full bg-ink py-4 text-lg font-semibold text-paper"
                        >
                            <Phone className="h-5 w-5" strokeWidth={2.5} />
                            {PHONE}
                        </motion.a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
